import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { REDUCER_ACTION_TYPES } from '../../store/Reducer.js';
import { Context } from '../../store/Store.js';
import { getProgramsForInsightsTable } from '../../services/programService.js';
import { ToastComponent } from '@tylertech/forge';
import { messageManager } from '../System/messageManager/messageManager.js';
import { FiltersContext } from '../reusable/Filters/filterContext.js';
import { useUser } from '../../contexts/userContext.js';

export const programInsightsContextInitState = {
  sets: [],
  currentSet: null,
  insightData: null,
  editDetailsTab: false,
  activeTab: 0,
  graphDrawerIsOpen: false,
  openDeleteDialog: false,
  infoForCreateInsightPage: null,
  successfullyCreatedInsight: null,
  generatedInsight: null,
};

export const ProgramInsightsContext = createContext(
  programInsightsContextInitState
);

const ProgramInsightsContextProvider = ({ children }) => {
  const [programs, setPrograms] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [sets, setSets] = useState([]);
  const [currentSet, setCurrentSet] = useState(null);
  const [insightData, setInsightData] = useState(null);
  const [editDetailsTab, setEditDetailsTab] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [graphDrawerIsOpen, setGraphDrawerIsOpen] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [infoForCreateInsightPage, setInfoForCreateInsightPage] = useState(null);
  const [successfullyCreatedInsight, setSuccessfullyCreatedInsight] = useState(null);
  const [selectedInsights, setSelectedInsights] = useState({});
  const {user} = useUser();
  const [ ,dispatch] = useContext(Context);
  const {appliedFilters} = useContext(FiltersContext);
  const [generatedInsight, setGeneratedInsight] = useState(null);

  const showRecommendButton = useMemo(() => {
    return Boolean(currentSet?.name?.toLowerCase() === 'impact' || currentSet?.name?.toLowerCase() === 'opportunity');
  },[currentSet]);

  const convertToDetailsPage = (insight) => {
    setGraphDrawerIsOpen(false);
    setActiveTab(0);
    setInsightData(insight);
  };

  const convertToCreateInsightPage = (programInfo, set, generatedInsightData = null) => {
    setGeneratedInsight(generatedInsightData);
    setInfoForCreateInsightPage({programInfo, set});
  };

  const handleGoBack = () => {
    setActiveTab(0);
    setInsightData(null);
    dispatch({ type: REDUCER_ACTION_TYPES.BACK_BUTTON_FIRED, payload: false });
  };

  const returnToDefaultTab = (index, insight) => {
    setActiveTab(index);
    setInfoForCreateInsightPage(null);
    setGeneratedInsight(null);
    setSuccessfullyCreatedInsight(insight);
  };

  useEffect(()=> {
    fetchPrograms();
  }, [currentSet, appliedFilters, user?.activeBudget?.id, user?.activeCostCenters]);

  const fetchPrograms = async () => {
    try {
      const { data } = await getProgramsForInsightsTable({
        appliedFiltersByTab: appliedFilters,
      });
      const programs = data.map((program) => {
        return {
          ...program,
          convertToCreateInsightPage: (generatedInsightData) =>
            convertToCreateInsightPage(program, currentSet, generatedInsightData),
        };
      });
      setPrograms(programs);
      setTotalCount(data.length);
    } catch (error) {
      ToastComponent.present({
        message: messageManager().PROGRAM_INSIGHTS.ERROR_FETCHING_PROGRAMS,
      });
    }
  };

  return (
    <ProgramInsightsContext.Provider
      value={{
        sets,
        setSets,
        currentSet,
        setCurrentSet,
        insightData,
        setInsightData,
        editDetailsTab,
        setEditDetailsTab,
        activeTab,
        setActiveTab,
        graphDrawerIsOpen,
        setGraphDrawerIsOpen,
        openDeleteDialog,
        setOpenDeleteDialog,
        infoForCreateInsightPage,
        setInfoForCreateInsightPage,
        convertToDetailsPage,
        convertToCreateInsightPage,
        handleGoBack,
        successfullyCreatedInsight,
        setSuccessfullyCreatedInsight,
        returnToDefaultTab,
        programs,
        totalCount,
        setTotalCount,
        selectedInsights,
        setSelectedInsights,
        showRecommendButton,
        generatedInsight,
        setGeneratedInsight
      }}
    >
      {children}
    </ProgramInsightsContext.Provider>
  );
};

ProgramInsightsContextProvider.propTypes = {
  children: PropTypes.node,
};

export default ProgramInsightsContextProvider;
